"use client"

import styled from "@emotion/styled"
import { yupResolver } from "@hookform/resolvers/yup"
import { Button, Checkbox, Grid, TextField } from "@mui/material"
import { postWebBmbyLead } from "api"
import { useLocale, useTranslations } from "next-intl"
import { LegacyRef, useRef, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import {
  CheckboxTransparentIcon,
  CheckedCheckboxTransparentIcon,
  NewText,
  SuccessFailStateLanding,
} from "ui"
import { submitClick, submitError, submitSuccess } from "utils"
import { generateGenericSchemaLanding } from "utils/Form/FormSchema"
import * as yup from "yup"
import CustomHelperText from "../Contact/CustomHelperText"
import { ProjectsMessages } from "../Projects/Projects"

const StyledWrapper = styled.div`
  background-color: #d3dceb;
`

const StyledForm = styled.form`
  .FormTitle {
    text-align: center;
    text-box-trim: trim-end;
    height: 41px;
  }
  ${(props) => props.theme.breakpoints.down("md")} {
    height: 100%;
    .FormTitle {
      margin-bottom: 0;
      text-align: left;
      height: unset;
      max-width: 226px;
    }
  }
  color: ${(props) => props.theme.colors.black};
  padding: 40px 28px 13px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 24px 29px 23px 30px;
    width: 100%;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    padding: 24px 30px;
  }
  .MuiFormHelperText-root {
    bottom: -22px;
    line-height: 1;
    p {
      margin-left: 10px !important;
      line-height: 18px;
    }
  }
  .MuiTextField-root {
    border-radius: 12.65px !important;
  }
  .MuiInputLabel-root {
    transform: translate(10px, -50%) scale(1);
    top: 50%;
    font-size: 18px;
    font-weight: 400;
    color: ${(props) => props.theme.colors.black};
    &.Mui-error {
      color: ${(props) => props.theme.colors.black};
    }
  }
  .MuiInputLabel-shrink {
    transform: translate(10px, -41px) scale(0.77);
    &.Mui-focused,
    &.Mui-error {
      color: ${(props) => props.theme.colors.black};
    }
  }
  .MuiFilledInput-input {
    height: 26px;
    padding: 7.5px 8px;
  }
  .MuiFilledInput-root.Mui-error {
    outline: 1px solid ${(props) => props.theme.colors.black};
    border: none;
  }
  .MuiFilledInput-root {
    border-radius: 12.65px !important;
    overflow: hidden;
  }
  .MuiGrid-root:not(.MuiGrid-item) {
    width: 100%;
    margin: unset;
  }
  .MuiGrid-item {
    padding-top: 0;
    padding-left: 0px !important;
    ${(props) => props.theme.breakpoints.up("md")} {
      &:nth-child(even) {
        padding-left: 4px !important;
      }
      &:nth-child(odd) {
        padding-right: 4px !important;
      }
    }
    ${(props) => props.theme.breakpoints.down("sm")} {
      &:nth-child(4) {
        padding-bottom: 0 !important;
      }
    }
  }
  .MuiGrid-item:not(.MuiGrid-item:last-child) {
    padding-bottom: 8px;
  }
`

const StyledCheckboxContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
`

const CheckBoxesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 21px;
  p {
    margin: 0;
  }
  a {
    color: ${(props) => props.theme.colors.black};
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-top: 16px;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    margin-top: 10px;
  }
`

const StyledCheckbox = styled(Checkbox, {
  shouldForwardProp: (prop) => prop !== "status",
})<{ status: boolean }>`
  padding: 0 8px 0px 0;
  ${(props) =>
    !props.status
      ? `  rect {
      stroke: red;
    }`
      : ""};
`

const BottomContainer = styled.div`
  ${(props) => props.theme.breakpoints.down("sm")} {
    .grecaptcha-badge {
      z-index: -1;
      height: 45px !important;
    }
  }
`
const StyledButton = styled(Button)`
  width: 100%;
  height: 43px;
  padding: 7.5px 8px;
  background-color: #4969a2;
  border: 1px solid #000000;
  border-radius: 12.65px;
  letter-spacing: -0.01em;
  :hover {
    background-color: #4969a2;
  }
`

type Props = {
  messages: ProjectsMessages
  formData?: {
    price: number | undefined
    currency: string | undefined
    area: number | undefined
    floor: string | undefined
    rooms: string | undefined
  }
  bmbyId?: number
  title?: string
  type?: string
  companyId?: number
  emailForLead?: string
  formTextForMeiling: string
  formTextForSms: string
  formTextForTerms: string
}

// eslint-disable-next-line prefer-destructuring
const RECAPTCHA_KEY = process.env.RECAPTCHA_KEY
if (!RECAPTCHA_KEY) {
  throw new Error("Please provide the RECAPTCHA_KEY environment variable.")
}

export const LandingLeadForm = ({
  messages,
  bmbyId,
  title,
  companyId,
  emailForLead,

  formTextForTerms,
}: Props) => {
  const [formIsSubmitted, setFormIsSubmitted] = useState(false)
  const [submitState, setSubmitState] = useState<"success" | "fail">("fail")
  const reCaptchaRef = useRef<ReCAPTCHA | null>()
  const locale = useLocale()
  const schema = generateGenericSchemaLanding(locale === "he" ? "he" : "en")
  const t = useTranslations("ProjectsPage")

  const [checkedCombined, setCheckedCombined] = useState<boolean>(false)

  type FormInput = yup.InferType<typeof schema>
  const {
    control,
    reset,
    handleSubmit,
    formState: { errors, isSubmitted, isSubmitting, isValid },
  } = useForm<FormInput>({
    resolver: yupResolver(schema),
    // Provide explicit defaults so fields are registered correctly and validation runs consistently
    defaultValues: { fullName: "", email: "", phone: "" },
    // Validate again on change AFTER the first submit attempt so users get instant feedback
    reValidateMode: "onChange",
    mode: "onSubmit",
  })
  const onSubmit: SubmitHandler<FormInput> = async (data) => {
    reCaptchaRef?.current?.reset()
    const recaptchaResponse = (await reCaptchaRef?.current?.executeAsync()) as string
    try {
      const senddata = {
        projectId: bmbyId || -1,
        name: data.fullName,
        email: data.email,
        phone: data.phone,
        comments: "",
        gRecaptchaResponse: recaptchaResponse,
        projectStatus: "",
        allowedMarketing: checkedCombined,
        allowedSmsAndEmail: checkedCombined,
        companyId: companyId || -1,
        uri: window?.location?.href,
        toEmail: emailForLead || "",
      }
      const response = await postWebBmbyLead(senddata)
      if (response?.data?.body?.addLeadSucceeded) {
        submitSuccess("שליחה", "השאירו פרטים ונחזור אליכם", true)
        setSubmitState("success")
        setFormIsSubmitted(true)
        reset()
      } else {
        setSubmitState("fail")
        setFormIsSubmitted(true)
        submitError("שליחה", messages.forYou, response?.data?.body?.errorMessage)
      }
    } catch (error) {
      setSubmitState("fail")
      setFormIsSubmitted(true)
      reset()
      submitError("שליחה", messages.forYou, error as any)
    }
  }
  return (
    <StyledWrapper>
      {formIsSubmitted ? (
        <div style={{ padding: "32px 22px" }}>
          <SuccessFailStateLanding
            setIsSubmitted={setFormIsSubmitted}
            state={submitState}
            messages={messages}
          />
        </div>
      ) : (
        <StyledForm onSubmit={handleSubmit(onSubmit)}>
          <NewText
            component="h2"
            desktopFontSize={27.5}
            desktopLineHeight="61.65px"
            desktopFontWeight={700}
            mobileFontSize={30.46}
            mobileLineHeight="26.93px"
            color="rgba(0, 7, 51, 1)"
            letterSpacing={"-0.01em"}
            className="FormTitle"
          >
            {title ? title : messages.forYou}
          </NewText>
          <Grid container style={{ marginTop: "24px" }}>
            <Grid item xs={12} md={6}>
              <Controller
                name="fullName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={messages.fullName}
                    variant="filled"
                    error={!!errors.fullName}
                    sx={{
                      marginBottom: errors.fullName?.message
                        ? "28px !important"
                        : field.value
                        ? "28px !important"
                        : "0 !important",
                    }}
                    helperText={
                      <CustomHelperText
                        render={isSubmitted || isSubmitting}
                        error={!!errors.fullName}
                        text={errors.fullName?.message}
                        hideIcons
                      />
                    }
                    fullWidth
                    inputProps={{ style: { fontSize: 18 } }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={messages.telephone}
                    variant="filled"
                    error={!!errors.phone}
                    inputProps={{ style: { fontSize: 18 } }}
                    sx={{
                      marginBottom: errors.phone?.message
                        ? "28px !important"
                        : field.value
                        ? "28px !important"
                        : "0px !important",
                    }}
                    helperText={
                      <CustomHelperText
                        render={isSubmitted || isSubmitting}
                        error={!!errors.phone}
                        text={errors.phone?.message}
                        hideIcons
                      />
                    }
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={messages.email}
                    variant="filled"
                    error={!!errors.email}
                    inputProps={{ style: { fontSize: 18 } }}
                    sx={{
                      marginBottom: errors.email?.message
                        ? "28px !important"
                        : field.value
                        ? "28px !important"
                        : "0px !important",
                    }}
                    helperText={
                      <CustomHelperText
                        render={isSubmitted || isSubmitting}
                        error={!!errors.email}
                        text={errors.email?.message}
                        hideIcons
                      />
                    }
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <StyledButton
                type="submit"
                variant="contained"
                size="large"
                style={{ marginInlineStart: "auto" }}
                disabled={isSubmitting}
                onClick={() => {
                  submitClick("שליחה", messages.forYou),
                    (Object.keys(errors).length > 0 || !isValid) &&
                      submitError(
                        "שליחה",
                        messages.forYou,
                        errors?.fullName?.message ||
                          errors?.email?.message ||
                          errors.phone?.message ||
                          "Fields are mandatory",
                      )
                }}
              >
                {isSubmitting ? t("sending") : t("send")}
              </StyledButton>
            </Grid>
            <Grid item xs={12}>
              <CheckBoxesContainer>
                <StyledCheckboxContainer>
                  <StyledCheckbox
                    icon={<CheckboxTransparentIcon />}
                    checkedIcon={<CheckedCheckboxTransparentIcon />}
                    sx={{
                      ":hover": {
                        backgroundColor: "transparent",
                      },
                    }}
                    status={true}
                    value={checkedCombined}
                    onChange={() => setCheckedCombined((el) => !el)}
                  />

                  <div>
                    <NewText
                      desktopFontSize={13}
                      desktopFontWeight="600"
                      desktopLineHeight="13px"
                      component="div"
                      color={"black"}
                      maxWidth={"750px"}
                      dangerouslySetInnerHTML={{
                        __html: `
                          ברצוני לקבל מידע שיווקי בדוא”ל, מסרונים או כל מדיה אחרת מפרויקט RISHO ו/ או הפניקס ו/או אשטרום נכסים או מי טעמם.<br/>
                          בשליחת טופס זה אני מאשר/ת שקראתי את מדיניות הפרטיות של אשטרום נכסים ו/או הפניקס או מי מטעמם ואת תנאי השימוש.
                        `,
                      }}
                    ></NewText>
                  </div>
                </StyledCheckboxContainer>
                <NewText
                  desktopFontSize={12}
                  desktopFontWeight="600"
                  desktopLineHeight="13px"
                  component="div"
                  color={"black"}
                  maxWidth={"750px"}
                  dangerouslySetInnerHTML={{
                    __html: formTextForTerms,
                  }}
                />
              </CheckBoxesContainer>
            </Grid>
          </Grid>
          <BottomContainer>
            {RECAPTCHA_KEY && (
              <ReCAPTCHA
                ref={reCaptchaRef as LegacyRef<ReCAPTCHA> | undefined}
                sitekey={RECAPTCHA_KEY}
                size="invisible"
                hl="iw"
                tabIndex={-1}
              />
            )}
          </BottomContainer>
        </StyledForm>
      )}
    </StyledWrapper>
  )
}
export default LandingLeadForm
